import { differenceInCalendarYears, getYear, parseISO } from "date-fns";
import type { DateValuePair } from "@rentablo/api/apiv1";

export interface ChartPoint {
  x: number;
  y: number;
  percent: number;
  isLocked: boolean;
  yearsAgo?: number;
}

const FREE_FAKE_YEARS = 4;
const PLUS_TARGET_TOTAL = 7;
const FAKE_ZIGZAG_OFFSETS = [0.08, -0.06, 0.12, -0.09, 0.07, -0.08];

const parsePercentsByYearsAgo = (quotes: DateValuePair[]) => {
  const today = new Date();
  const currentYear = getYear(today);

  return quotes.reduce<Map<number, number>>((byYearsAgo, q) => {
    if (!q.date || q.value === undefined) return byYearsAgo;
    const date = parseISO(q.date);
    if (getYear(date) === currentYear) return byYearsAgo;
    const yearsAgo = differenceInCalendarYears(today, date);
    if (yearsAgo < 1 || byYearsAgo.has(yearsAgo)) return byYearsAgo;
    byYearsAgo.set(yearsAgo, q.value);
    return byYearsAgo;
  }, new Map());
};

const getFakeTailCount = (
  isUserPro: boolean,
  isUserPlus: boolean,
  lastRealYear: number
) => {
  if (isUserPro) return 0;
  if (isUserPlus) return Math.max(0, PLUS_TARGET_TOTAL - lastRealYear);
  return FREE_FAKE_YEARS;
};

const getFakePercent = (
  yearsAgo: number,
  lastRealYear: number,
  targetLength: number,
  lastRealPercent: number
) => {
  const stepsPast = yearsAgo - lastRealYear;
  const offsetIndex =
    (((stepsPast - 1) % FAKE_ZIGZAG_OFFSETS.length) +
      FAKE_ZIGZAG_OFFSETS.length) %
    FAKE_ZIGZAG_OFFSETS.length;
  const rawOffset = FAKE_ZIGZAG_OFFSETS[offsetIndex];
  const isLastFake = yearsAgo === targetLength;
  return lastRealPercent + (isLastFake ? Math.abs(rawOffset) : rawOffset);
};

const makePoint = (
  x: number,
  yearsAgo: number,
  percent: number,
  isLocked: boolean
): ChartPoint => ({
  x,
  y: percent * 100,
  percent,
  isLocked,
  yearsAgo,
});

export const buildPoints = (
  quotes: DateValuePair[],
  isUserPro: boolean,
  isUserPlus: boolean
): ChartPoint[] => {
  const byYearsAgo = parsePercentsByYearsAgo(quotes);
  if (byYearsAgo.size === 0) return [];

  const lastRealYear = Math.max(0, ...Array.from(byYearsAgo.keys()));
  const fakeCount = getFakeTailCount(isUserPro, isUserPlus, lastRealYear);
  const lastRealPercent = byYearsAgo.get(lastRealYear) ?? 0;

  const points: ChartPoint[] = [];
  let xPosition = 1;

  for (let yearsAgo = 1; yearsAgo <= lastRealYear; yearsAgo++) {
    const realPercent = byYearsAgo.get(yearsAgo);
    if (realPercent !== undefined) {
      points.push(makePoint(xPosition++, yearsAgo, realPercent, false));
    }
  }

  for (let yearsAgo = 0; yearsAgo > 0 - fakeCount; yearsAgo--) {
    const fakePercent = getFakePercent(
      yearsAgo,
      lastRealYear,
      lastRealYear + fakeCount,
      lastRealPercent
    );
    points.push(makePoint(xPosition++, yearsAgo, fakePercent, true));
  }

  return points;
};
